import { useButton } from '@react-aria/button'
import { useTab, useTabList, useTabPanel } from '@react-aria/tabs'
import { useObjectRef } from '@react-aria/utils'
import { useTabListState } from '@react-stately/tabs'
import { SidebarRight } from 'almanac-ui/icons'
import cx from 'classnames'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { observer } from 'mobx-react-lite'
import { RIGHT_RAIL_TABS_CONTAINER_ID } from './constants'
import { TabPanelProps, TabProps, TabsProps } from './Types'
import { useLayout } from 'stores/LayoutStore'
import Styles from './Tabs.module.scss'
import { FocusModeHidden } from 'components/FocusModeHidden/FocusModeHidden'
import { useArticleView } from 'providers/ArticleViewProvider/ArticleViewProvider'
import { ArticleView, UserRole } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { NotificationsTrayContainer } from 'blaze-components/NotificationsTray/NotificationsTrayContainer'
import { NotificationsWriterTrigger } from 'blaze-components/NotificationsTray/NotificationsWriterTrigger'
import { CounterVariant } from 'blaze-ui/Counter/Counter'
import { CanView } from 'components/Can'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { useRootStore } from 'stores/RootStore'
import { ProfileDropdown } from 'blaze-components/TopBar/ProfileDropdown/ProfileDropdown'
import { useHeaderTheme } from 'hooks/useHeaderTheme'
import { VirtualMarketerUpgradeButton } from 'blaze-components/VirtualMarketerUpgradeButton/VirtualMarketerUpgradeButton'
import { AutopilotProUpgradeButton } from 'components/AutopilotUpgradeButtons'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { CreditUsageDropdown } from 'blaze-components/CreditUsageDropdown/CreditUsageDropdown'
import { SegmentedControl } from 'blaze-ui/SegmentedControl/SegmentedControl'
import { Item } from '@react-stately/collections'
import { ItemElement } from '@react-types/shared'

const TabPanel = <T,>({ state, ...props }: TabPanelProps<T>) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const articleView = useArticleView()
  const ref = useObjectRef<HTMLDivElement | null>(null)
  const { tabPanelProps } = useTabPanel(props, state, ref)

  const commentsTab = state.collection.getItem('Comments')
  const aiAssistantTab = state.collection.getItem('AIAssistant')
  const metadataTab = state.collection.getItem('Metadata')

  return (
    <FocusModeHidden {...tabPanelProps} ref={ref} className={Styles.tabPanel}>
      {isSystemRedesignEnabled && articleView !== ArticleView.Commenting && (
        <div className={Styles.segmentedControlContainer}>
          <SegmentedControl
            className={Styles.toggleElement}
            activeItem={state.selectedKey}
            onItemClick={(value) => state.setSelectedKey(value)}
          >
            {
              [
                aiAssistantTab && (
                  <Item key="AIAssistant">
                    <div className={Styles.segmentedControlItem}>Actions</div>
                  </Item>
                ),
                metadataTab && (
                  <Item key="Metadata">
                    <div className={Styles.segmentedControlItem}>Metadata</div>
                  </Item>
                ),
                commentsTab && (
                  <Item key="Comments">
                    <div className={Styles.segmentedControlItem}>Comments</div>
                  </Item>
                ),
              ].filter(Boolean) as ItemElement<HTMLElement>[]
            }
          </SegmentedControl>
        </div>
      )}

      {state.selectedItem?.props.children}
    </FocusModeHidden>
  )
}

const Tab = observer(<T,>({ item, state }: TabProps<T>) => {
  const { key, rendered } = item
  const ref = useObjectRef<HTMLDivElement | null>(null)
  const { tabProps } = useTab({ key }, state, ref)
  const isSelected = state.selectedKey === key

  return (
    <Tooltip label={item['aria-label']} placement="top">
      <div
        className={cx(Styles.tab, { [Styles.selected]: isSelected })}
        ref={ref}
        {...tabProps}
        aria-label={item['aria-label']}
      >
        {rendered}
      </div>
    </Tooltip>
  )
})

const CollapseTab = observer(() => {
  const ref = useObjectRef<HTMLDivElement | null>(null)
  const layout = useLayout()
  const { rightRailPanelStore } = useRootStore()
  const { buttonProps } = useButton(
    {
      onPress: () => {
        layout.setRightColumnLocked(false)
        rightRailPanelStore.hide()
      },
    },
    ref,
  )

  return (
    <Tooltip label="Close" placement="top">
      <div
        className={cx(Styles.tab, Styles.collapseTab, {
          [Styles.hidden]: !layout.rightColumnLocked || !layout.rightColumnIsHovered,
        })}
        ref={ref}
        {...buttonProps}
      >
        <SidebarRight />
      </div>
    </Tooltip>
  )
})

export const Tabs = observer((props: TabsProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const state = useTabListState(props)
  const ref = useObjectRef<HTMLDivElement | null>(null)
  const { tabListProps } = useTabList(props, state, ref)
  const layout = useLayout()
  const articleView = useArticleView()
  const articleMeta = useCurrentArticleMeta()
  const currentWorkspace = useCurrentWorkspace()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const workspaces = useWorkspaces()
  const theme = useHeaderTheme()

  if (state.collection.size === 0) {
    return <div ref={ref} />
  }

  const commentsTab = state.collection.getItem('Comments')
  const aiAssistantTab = state.collection.getItem('AIAssistant')
  const currentTab = state.selectedItem?.key

  return (
    <div
      onMouseEnter={() => layout.setRightColumnHovered(true)}
      onMouseLeave={() => layout.setRightColumnHovered(false)}
      className={cx(Styles.container, Styles[articleView || ArticleView.Reading])}
      id={RIGHT_RAIL_TABS_CONTAINER_ID}
    >
      <FocusModeHidden {...tabListProps} data-theme={theme} ref={ref} className={Styles.tabList}>
        <div className={Styles.leftTabs}>
          {articleMeta?.company_id && (
            <CanView
              as={[UserRole.GUEST, UserRole.MEMBER, UserRole.SUPER_ADMIN, UserRole.ADMIN]}
              inWorkspace={articleMeta.company_id}
            >
              <CollapseTab />
            </CanView>
          )}
        </div>
        <div className={Styles.tabs}>
          {!isSystemRedesignEnabled && articleView !== ArticleView.Commenting && (
            <>
              {currentTab === 'AIAssistant' && commentsTab && <Tab key="Comments" item={commentsTab} state={state} />}
              {currentTab === 'Comments' && aiAssistantTab && (
                <Tab key="AIAssistant" item={aiAssistantTab} state={state} />
              )}
            </>
          )}

          {articleMeta?.company_id && (
            <CanView as={[UserRole.MEMBER, UserRole.SUPER_ADMIN, UserRole.ADMIN]} inWorkspace={articleMeta?.company_id}>
              {currentWorkspace?.is_ap_freemium ? (
                <AutopilotProUpgradeButton variant="upgrade" />
              ) : (
                <VirtualMarketerUpgradeButton showTrialDaysLeft={false} />
              )}
              {!isSystemRedesignEnabled && (
                <NotificationsTrayContainer
                  trigger={<NotificationsWriterTrigger />}
                  counterVariant={CounterVariant.Dark}
                />
              )}
              {isSystemRedesignEnabled && <CreditUsageDropdown />}
              <ProfileDropdown selectedWorkspaceId={currentWorkspaceId} workspaces={workspaces} />
            </CanView>
          )}
        </div>
        {articleMeta?.company_id && (
          <CanView as={[UserRole.UNAUTHED]} inWorkspace={articleMeta?.company_id}>
            <CollapseTab />
          </CanView>
        )}
      </FocusModeHidden>
      <TabPanel state={state} />
    </div>
  )
})
